import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import { Redirect } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/modal.css";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import Iframe from "react-iframe";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HEADER_ACTIONS_STYLE = {
  display: "flex",
  gap: "8px",
  alignItems: "center",
};

const HEADER_BTN_STYLE = {
  fontSize: "12px",
  fontWeight: "700",
  height: "32px",
  minWidth: "72px",
};

const PREVIEW_TITLE_STYLE = {
  margin: "0 0 8px",
  fontSize: "15px",
  fontWeight: "800",
  color: COLORS.primary_color_shade8,
  textAlign: "center",
};

const DESIGN_TITLE_STYLE = {
  margin: "0 0 10px",
  fontSize: "15px",
  fontWeight: "800",
  color: COLORS.primary_color_shade8,
};

const MdlPrintableSettings = ({
  sample_id,
  color_option_id,
  actionClose,
  actionDone,
  file_url,
  title = "Printable Design Settings",
  settingsApi = "getPrintableSampleData.php",
}) => {
  const [loggedIn, setLoggedIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(true);
  const [sampleDesignSettings, setSampleDesignSettings] = useState([]);
  const [originalSampleSettings, setOriginalSampleSettings] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(file_url);
  const [showPreview, setShowPreview] = useState(true);
  const [iframeKey, setIframeKey] = useState(Date.now());
  const [height, setHeight] = useState("300px");
  const iframeRef = useRef(null);

  const loadSettings = useCallback(() => {
    setLoading(true);

    axios
      .get(`${API_BASE()}${settingsApi}`, {
        params: {
          school_code: SessionManager.getSchoolCode(),
          sample_id,
          color_option_id,
        },
      })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);

        if (response.api_status === "OK") {
          const designSettings = Array.isArray(response.data)
            ? response.data
            : response.data?.Sample_Design_Settings || [];
          const filteredSettings = designSettings.filter(
            (item) => item.show_settings === "1"
          );
          setSampleDesignSettings(filteredSettings);
          setOriginalSampleSettings(JSON.parse(JSON.stringify(filteredSettings)));
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error loading printable settings:", error);
      });
  }, [sample_id, color_option_id, settingsApi]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoggedIn(false);
    }

    loadSettings();

    const handleMessage = (event) => {
      if (event.data?.iframeHeight) {
        setHeight(`${event.data.iframeHeight}px`);
      }
    };

    window.addEventListener("message", handleMessage, false);
    return () => window.removeEventListener("message", handleMessage);
  }, [loadSettings]);

  useEffect(() => {
    setPreviewUrl(file_url);
  }, [file_url]);

  const handleClose = () => {
    setShow(false);
    actionClose();
  };

  const handleDone = () => {
    setShow(false);
    if (actionDone) {
      actionDone();
    } else {
      actionClose();
    }
  };

  const handleRefreshPreview = () => {
    setShowPreview(true);
    setIframeKey(Date.now());
  };

  const handleAttrChange = (settingIndex, attrIndex, value) => {
    setSampleDesignSettings((prev) => {
      const updatedSettings = [...prev];
      updatedSettings[settingIndex].attribute[attrIndex].attribute_value = value;
      return updatedSettings;
    });
  };

  const getModifiedSettings = () => {
    const modified = [];

    sampleDesignSettings.forEach((currentSetting, settingIndex) => {
      const originalSetting = originalSampleSettings[settingIndex];
      const changedAttributes = (currentSetting.attribute || []).filter(
        (attr, attrIndex) =>
          attr.attribute_value !==
          originalSetting?.attribute?.[attrIndex]?.attribute_value
      );

      if (changedAttributes.length > 0) {
        modified.push({
          ...currentSetting,
          attribute: changedAttributes,
        });
      }
    });

    return modified;
  };

  const applySettingChange = () => {
    const changedSettings = getModifiedSettings();

    if (changedSettings.length === 0) {
      myToast.notify_info("No changes detected.");
      return;
    }

    setLoading(true);

    const staff = SessionManager.getStaffDetails();
    const session = SessionManager.getCurrentSession();
    const params = new URLSearchParams();

    params.append("staff_id", staff.staff_id);
    params.append("school_code", SessionManager.getSchoolCode());
    params.append("sample_id", sample_id);
    params.append("color_option_id", color_option_id);
    params.append("session_id", session[0].session_id);
    params.append("sample_settings", JSON.stringify(changedSettings));

    axios
      .post(`${API_BASE()}AddPrintableSampleSettings.php`, params)
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);

        if (response.api_status === "OK") {
          myToast.notify_success("Design settings updated.");
          handleRefreshPreview();
          setOriginalSampleSettings(JSON.parse(JSON.stringify(sampleDesignSettings)));
        } else {
          myToast.notify_warning(response.api_remark);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error applying printable settings:", error);
        myToast.notify_error("Something went wrong while saving settings.");
      });
  };

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      animation={false}
      className="fullscreen-modal hide-scrollbar"
      dialogClassName="fullscreen-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>

        <div style={HEADER_ACTIONS_STYLE}>
          <Button
            className="margin_0 gravity_center"
            variant="outline-secondary"
            onClick={handleClose}
            style={{ fontSize: "2rem", height: "30px" }}
            title="Close"
          >
            &times;
          </Button>
          <Button
            className="margin_0 gravity_center"
            variant="outline-secondary"
            onClick={loadSettings}
            style={{ fontSize: "1.5rem", height: "30px" }}
            title="Refresh"
          >
            🔄
          </Button>
          <Button
            style={HEADER_BTN_STYLE}
            className="margin_0"
            variant="outline-secondary"
            size="sm"
            onClick={handleDone}
          >
            Done
          </Button>
          <Button
            style={HEADER_BTN_STYLE}
            className="margin_0"
            variant="primary"
            size="sm"
            onClick={applySettingChange}
          >
            Apply
          </Button>
        </div>
      </Modal.Header>

      <Modal.Body className="hide-scrollbar">
        {loading ? <MyLoader /> : null}

        <div className="row settings-scroll">
          <div className="col-md-5 col-sm-5 padding_0">
            <h3 style={PREVIEW_TITLE_STYLE}>Preview</h3>
            <div style={{ position: "relative", width: "100%" }}>
              {showPreview ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.6)",
                    zIndex: 10,
                  }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading</span>
                  </div>
                </div>
              ) : null}

              <Iframe
                key={iframeKey}
                ref={iframeRef}
                src={previewUrl}
                width="100%"
                height={height}
                style={{ border: "none", overflow: "hidden" }}
                id="printableSettingsPreviewFrame"
                scrolling="no"
                onLoad={() => setShowPreview(false)}
              />
            </div>
          </div>

          <div className="col-md-7 col-sm-7 padding_0">
            <h3 style={DESIGN_TITLE_STYLE}>Design Customization</h3>
            {sampleDesignSettings.length === 0 ? (
              <div className="bg_card_primary_outline padding_10">
                No design settings available for this template.
              </div>
            ) : (
              sampleDesignSettings.map((setting, settingIndex) => (
                <div
                  key={setting.setting_id || settingIndex}
                  className="bg_card_primary_outline shadow-sm"
                  style={{ marginBottom: "8px" }}
                >
                  <div
                    className="bg_card_primary text-white d-flex justify-content-between align-items-center"
                    style={{
                      height: "35px",
                      fontSize: "16px",
                      padding: "0 10px",
                    }}
                  >
                    <strong>{setting.setting_name}</strong>
                  </div>

                  <div className="card-body padding_10">
                    <div className="row">
                      {(setting.attribute || []).map((attr, attrIndex) => {
                        let options = null;

                        try {
                          if (attr.data_set) {
                            options = Array.isArray(attr.data_set)
                              ? attr.data_set
                              : JSON.parse(attr.data_set);
                          }
                        } catch (error) {
                          console.error("Invalid data_set JSON:", attr.data_set);
                        }

                        const isHexColor =
                          typeof attr.attribute_value === "string" &&
                          /^#([0-9A-F]{3}){1,2}$/i.test(attr.attribute_value);

                        return (
                          <div className="col-md-3 padding_2" key={attrIndex}>
                            <div className="mb-2">
                              <label className="form-label">{attr.attribute_name}</label>

                              {options ? (
                                <select
                                  className="form-select"
                                  value={attr.attribute_value}
                                  onChange={(event) =>
                                    handleAttrChange(
                                      settingIndex,
                                      attrIndex,
                                      event.target.value
                                    )
                                  }
                                >
                                  <option value="">-- Select --</option>
                                  {options.map((option, optionIndex) => (
                                    <option key={optionIndex} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </select>
                              ) : isHexColor ? (
                                <div className="d-flex align-items-center gap-1">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={attr.attribute_value}
                                    onChange={(event) =>
                                      handleAttrChange(
                                        settingIndex,
                                        attrIndex,
                                        event.target.value
                                      )
                                    }
                                  />
                                  <input
                                    type="color"
                                    className="form-control form-control-color"
                                    value={attr.attribute_value}
                                    onChange={(event) =>
                                      handleAttrChange(
                                        settingIndex,
                                        attrIndex,
                                        event.target.value
                                      )
                                    }
                                    style={{
                                      width: "50px",
                                      height: "32px",
                                      padding: "1px",
                                      border: "1px solid #ced4da",
                                      borderRadius: "4px",
                                      background: "none",
                                    }}
                                  />
                                </div>
                              ) : (
                                <input
                                  type="text"
                                  className="form-control"
                                  value={attr.attribute_value}
                                  onChange={(event) =>
                                    handleAttrChange(
                                      settingIndex,
                                      attrIndex,
                                      event.target.value
                                    )
                                  }
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default MdlPrintableSettings;
